import React, { useState, useEffect } from 'react';
import dossierService from '../../../services/dossierService';
import DocumentCard from '../../property-dossier/components/DocumentCard';
import MilestoneCard from '../../property-dossier/components/MilestoneCard';
import DocumentUploadModal from '../../property-dossier/components/DocumentUploadModal';
import DocumentViewerModal from '../../property-dossier/components/DocumentViewerModal';
import MilestoneForm from '../../property-dossier/components/MilestoneForm';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DossierTab = ({ property, user, subscriptionTier }) => {
    // Documents
    const [documents, setDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [viewingDocument, setViewingDocument] = useState(null);
    const [documentFilters, setDocumentFilters] = useState({ type: '', search: '' });

    // Milestones
    const [milestones, setMilestones] = useState([]);
    const [loadingMilestones, setLoadingMilestones] = useState(false);
    const [showMilestoneForm, setShowMilestoneForm] = useState(false);
    const [editingMilestone, setEditingMilestone] = useState(null);
    const [milestoneFilters, setMilestoneFilters] = useState({ category: '', status: '' });

    // Active internal tab
    const [activeSubTab, setActiveSubTab] = useState('documents');

    // Quick adds forms state
    const [mortgageData, setMortgageData] = useState({});
    const [insuranceData, setInsuranceData] = useState({});
    const [maintenanceData, setMaintenanceData] = useState({});

    // Load documents
    useEffect(() => {
        const loadDocuments = async () => {
            if (!property?.id || !user?.id) return;

            setLoadingDocuments(true);
            try {
                const result = await dossierService.getDocuments(property.id, user.id, documentFilters);
                if (result?.success) {
                    setDocuments(result.data || []);
                }
            } catch (error) {
                console.error('Error loading documents:', error);
            } finally {
                setLoadingDocuments(false);
            }
        };

        loadDocuments();
    }, [property?.id, user?.id, documentFilters.type, documentFilters.search, subscriptionTier]);

    // Load milestones
    useEffect(() => {
        const loadMilestones = async () => {
            if (!property?.id || !user?.id) return;

            setLoadingMilestones(true);
            try {
                const result = await dossierService.getMilestones(property.id, user.id, milestoneFilters);
                if (result?.success) {
                    setMilestones(result.data || []);
                }
            } catch (error) {
                console.error('Error loading milestones:', error);
            } finally {
                setLoadingMilestones(false);
            }
        };

        loadMilestones();
    }, [property?.id, user?.id, milestoneFilters.status, milestoneFilters.category, subscriptionTier]);

    const handleUploadDocument = async (documentData) => {
        if (!property?.id || !user?.id) return;

        try {
            const result = await dossierService.uploadDocument(
                property.id,
                user.id,
                documentData.file,
                {
                    type: documentData.type,
                    title: documentData.title,
                    description: documentData.description,
                    tags: documentData.tags
                }
            );

            if (result?.success) {
                setShowDocumentModal(false);
                // Reload documents
                const reloadResult = await dossierService.getDocuments(property.id, user.id, documentFilters);
                if (reloadResult?.success) {
                    setDocuments(reloadResult.data || []);
                }
            } else {
                alert(result?.error || 'Erreur lors de l\'upload');
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            alert('Erreur lors de l\'upload du document');
        }
    };

    const handleDeleteDocument = async (document) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${document.title}" ?`)) return;

        try {
            const result = await dossierService.deleteDocument(document.id, user?.id);
            if (result?.success) {
                setDocuments(prev => prev.filter(d => d.id !== document.id));
            } else {
                alert(result?.error || 'Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            alert('Erreur lors de la suppression');
        }
    };

    const handleSaveMilestone = async (milestoneData) => {
        if (!property?.id || !user?.id) return;

        try {
            let result;
            if (editingMilestone) {
                result = await dossierService.updateMilestone(editingMilestone.id, user.id, milestoneData);
            } else {
                result = await dossierService.createMilestone(property.id, user.id, milestoneData);
            }

            if (result?.success) {
                setShowMilestoneForm(false);
                setEditingMilestone(null);
                setActiveSubTab('timeline');
                // Reload milestones
                const reloadResult = await dossierService.getMilestones(property.id, user.id, milestoneFilters);
                if (reloadResult?.success) {
                    setMilestones(reloadResult.data || []);
                }
            } else {
                alert(result?.error || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Error saving milestone:', error);
        }
    };

    const handleMarkMilestoneDone = async (milestone) => {
        try {
            const result = await dossierService.markMilestoneDone(milestone.id, user?.id);
            if (result?.success) {
                setMilestones(prev => prev.map(m => m.id === milestone.id ? { ...m, status: 'DONE' } : m));
            }
        } catch (error) {
            console.error('Error marking milestone done:', error);
        }
    };

    const handleDeleteMilestone = async (milestone) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${milestone.name}" ?`)) return;

        try {
            const result = await dossierService.deleteMilestone(milestone.id, user?.id);
            if (result?.success) {
                setMilestones(prev => prev.filter(m => m.id !== milestone.id));
            }
        } catch (error) {
            console.error('Error deleting milestone:', error);
        }
    };

    const handleSaveMortgage = async () => {
        if (!mortgageData.due_date) {
            alert('Veuillez entrer une date de renouvellement');
            return;
        }
        const ms = {
            category: 'HYPOTHEQUE',
            name: 'Renouvellement hypothécaire',
            due_date: mortgageData.due_date,
            meta: { ...mortgageData }
        };
        await handleSaveMilestone(ms);
        setMortgageData({});
    };

    const handleSaveInsurance = async () => {
        if (!insuranceData.due_date) {
            alert('Veuillez entrer une date de renouvellement');
            return;
        }
        const ms = {
            category: 'ASSURANCE',
            name: 'Renouvellement assurance',
            due_date: insuranceData.due_date,
            amount: insuranceData.premium ? parseFloat(insuranceData.premium) : null,
            meta: { ...insuranceData }
        };
        await handleSaveMilestone(ms);
        setInsuranceData({});
    };

    const subTabs = [
        { id: 'documents', label: 'Documents', icon: 'FileText' },
        { id: 'timeline', label: 'Calendrier', icon: 'Calendar' },
        { id: 'quick_add', label: 'Gestion', icon: 'Settings' }
    ];

    if (false) {
        return (
            <div className="bg-card border border-border rounded-xl p-12 text-center shadow-sm">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Lock" size={32} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">Dossier Immeuble (Plan Plus)</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Centralisez vos documents, baux, contrats et suivez vos échéances de renouvellement directement dans votre analyse.
                </p>
                <Button variant="default" size="lg" iconName="Crown" onClick={() => window.location.href = '/subscription-pricing'}>
                    Passer à Plus
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex p-1 bg-muted rounded-xl overflow-x-auto no-scrollbar border border-border/50">
                    {subTabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveSubTab(tab.id)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${activeSubTab === tab.id
                                ? 'bg-background text-primary shadow-sm border border-border/50'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10'
                                }`}
                        >
                            <Icon name={tab.icon} size={18} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="flex gap-2">
                    {activeSubTab === 'documents' && (
                        <Button
                            size="sm"
                            iconName="Upload"
                            onClick={() => setShowDocumentModal(true)}
                            className="px-4 shadow-sm"
                        >
                            Ajouter un document
                        </Button>
                    )}
                    {activeSubTab === 'timeline' && (
                        <Button
                            size="sm"
                            iconName="Plus"
                            onClick={() => {
                                setEditingMilestone(null);
                                setShowMilestoneForm(true);
                            }}
                            className="px-4 shadow-sm"
                        >
                            Nouvelle échéance
                        </Button>
                    )}
                </div>
            </div>

            {activeSubTab === 'documents' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-muted/30 p-4 rounded-xl border border-border/50">
                        <div className="md:col-span-3">
                            <Input
                                placeholder="Rechercher par titre ou tag..."
                                iconName="Search"
                                value={documentFilters.search}
                                onChange={(e) => setDocumentFilters(prev => ({ ...prev, search: e.target.value }))}
                                className="w-full bg-background"
                            />
                        </div>
                        <div>
                            <Select
                                value={documentFilters.type}
                                onChange={(val) => setDocumentFilters(prev => ({ ...prev, type: val }))}
                                className="bg-background"
                                options={[
                                    { value: '', label: 'Tous les types' },
                                    { value: 'OFFRE', label: 'Offre d\'achat' },
                                    { value: 'BAIL', label: 'Bail / Location' },
                                    { value: 'INSPECTION', label: 'Inspection' },
                                    { value: 'FINANCES', label: 'Finance / Relevé' },
                                    { value: 'TAXES', label: 'Taxes Scolaires/Municipales' },
                                    { value: 'AUTRE', label: 'Autre document' }
                                ]}
                            />
                        </div>
                    </div>

                    {loadingDocuments ? (
                        <div className="flex flex-col items-center justify-center py-20 grayscale opacity-50">
                            <Icon name="Loader2" size={48} className="animate-spin text-primary mb-4" />
                            <p className="text-muted-foreground font-medium">Chargement des documents...</p>
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="text-center py-20 bg-muted/20 rounded-2xl border-2 border-dashed border-border/50">
                            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                                <Icon name="FileText" size={32} className="text-muted-foreground" />
                            </div>
                            <p className="text-muted-foreground font-medium text-lg">Aucun document dans ce dossier</p>
                            <p className="caption text-muted-foreground/60 mb-6">Importez vos offres, baux et rapports d'inspection ici.</p>
                            <Button size="sm" variant="outline" iconName="Upload" onClick={() => setShowDocumentModal(true)}>Uploader mon premier fichier</Button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                            {documents.map(doc => (
                                <DocumentCard
                                    key={doc.id}
                                    document={doc}
                                    onView={() => setViewingDocument(doc)}
                                    onDelete={() => handleDeleteDocument(doc)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeSubTab === 'timeline' && (
                <div className="space-y-6">
                    <div className="inline-flex gap-1 p-1 bg-muted rounded-lg border border-border/50">
                        {[
                            { id: '', label: 'Toutes' },
                            { id: 'PENDING', label: 'En cours' },
                            { id: 'DONE', label: 'Complétées' }
                        ].map(filter => (
                            <button
                                key={filter.id}
                                onClick={() => setMilestoneFilters(prev => ({ ...prev, status: filter.id }))}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${milestoneFilters.status === filter.id
                                    ? 'bg-background text-primary shadow-sm'
                                    : 'text-muted-foreground hover:bg-background/50'
                                    }`}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>

                    {loadingMilestones ? (
                        <div className="flex flex-col items-center justify-center py-20 grayscale opacity-50">
                            <Icon name="Loader2" size={48} className="animate-spin text-primary mb-4" />
                            <p className="text-muted-foreground font-medium">Chargement du calendrier...</p>
                        </div>
                    ) : milestones.length === 0 ? (
                        <div className="text-center py-20 bg-muted/20 rounded-2xl border-2 border-dashed border-border/50">
                            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                                <Icon name="Calendar" size={32} className="text-muted-foreground" />
                            </div>
                            <p className="text-muted-foreground font-medium text-lg">Aucune échéance à venir</p>
                            <p className="caption text-muted-foreground/60 mb-6">Suivez les renouvellements de baux, d'assurances et d'hypothèques.</p>
                            <Button size="sm" variant="outline" iconName="Plus" onClick={() => setShowMilestoneForm(true)}>Ajouter une échéance</Button>
                        </div>
                    ) : (
                        <div className="space-y-4 max-w-4xl">
                            {milestones.map(m => (
                                <MilestoneCard
                                    key={m.id}
                                    milestone={m}
                                    onDone={() => handleMarkMilestoneDone(m)}
                                    onEdit={() => {
                                        setEditingMilestone(m);
                                        setShowMilestoneForm(true);
                                    }}
                                    onDelete={() => handleDeleteMilestone(m)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeSubTab === 'quick_add' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in slide-in-from-bottom-4 duration-500">
                    {/* Section Hypothèque */}
                    <div className="bg-card border border-border/60 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                                <Icon name="Target" className="text-primary" size={24} />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold">Hypothèque & Prêt</h4>
                                <p className="text-xs text-muted-foreground">Suivi des renouvellements de taux</p>
                            </div>
                        </div>

                        <div className="space-y-5">
                            <Input label="Prêteur" placeholder="Ex: RBC, Desjardins..." value={mortgageData.lender || ''} onChange={(e) => setMortgageData({ ...mortgageData, lender: e.target.value })} />
                            <div className="grid grid-cols-2 gap-4">
                                <Input label="Taux actuel (%)" type="number" step="0.01" value={mortgageData.rate || ''} onChange={(e) => setMortgageData({ ...mortgageData, rate: e.target.value })} />
                                <Input label="Date renouvellement" type="date" value={mortgageData.due_date || ''} onChange={(e) => setMortgageData({ ...mortgageData, due_date: e.target.value })} />
                            </div>
                            <Input label="Solde estimé ($)" type="number" placeholder="0" value={mortgageData.balance || ''} onChange={(e) => setMortgageData({ ...mortgageData, balance: e.target.value })} />
                            <Button fullWidth iconName="Save" onClick={handleSaveMortgage}>Enregistrer dans le calendrier</Button>
                        </div>
                    </div>

                    {/* Section Assurance */}
                    <div className="bg-card border border-border/60 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                                <Icon name="Shield" className="text-accent" size={24} />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold">Assurance</h4>
                                <p className="text-xs text-muted-foreground">Gestion de la police d'assurance</p>
                            </div>
                        </div>

                        <div className="space-y-5">
                            <Input label="Compagnie d'assurance" placeholder="Ex: Intact, Beneva..." value={insuranceData.insurer || ''} onChange={(e) => setInsuranceData({ ...insuranceData, insurer: e.target.value })} />
                            <div className="grid grid-cols-2 gap-4">
                                <Input label="Prime ($/an)" type="number" value={insuranceData.premium || ''} onChange={(e) => setInsuranceData({ ...insuranceData, premium: e.target.value })} />
                                <Input label="Date renouvellement" type="date" value={insuranceData.due_date || ''} onChange={(e) => setInsuranceData({ ...insuranceData, due_date: e.target.value })} />
                            </div>
                            <Input label="No de police" value={insuranceData.policy_number || ''} onChange={(e) => setInsuranceData({ ...insuranceData, policy_number: e.target.value })} />
                            <Button fullWidth variant="outline" iconName="Save" onClick={handleSaveInsurance}>Enregistrer dans le calendrier</Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            {showDocumentModal && (
                <DocumentUploadModal
                    isOpen={showDocumentModal}
                    onClose={() => setShowDocumentModal(false)}
                    onUpload={handleUploadDocument}
                />
            )}
            {viewingDocument && (
                <DocumentViewerModal
                    isOpen={Boolean(viewingDocument)}
                    onClose={() => setViewingDocument(null)}
                    document={viewingDocument}
                />
            )}
            {showMilestoneForm && (
                <MilestoneForm
                    isOpen={showMilestoneForm}
                    onClose={() => {
                        setShowMilestoneForm(false);
                        setEditingMilestone(null);
                    }}
                    onSave={handleSaveMilestone}
                    milestone={editingMilestone}
                />
            )}
        </div>
    );
};

export default DossierTab;
